import apiClient from './apiClient';

export interface KPI {
  name: string;
  value: number | string | null;
  change?: number;
  changeLabel?: string;
  unit?: string;
  category: string;
}

export interface ProjectHealthScore {
  overall: number;
  financial: number;
  schedule: number;
  quality: number;
  compliance: number;
}

export interface CashFlowData {
  month: string;
  actual: number;
  projected: number;
}

export interface PhaseProgress {
  id: string;
  name: string;
  progress: number;
  status: string;
}

export interface RiskItem {
  id: string;
  title: string;
  score: number;
  owner: string;
  status: string;
  category: string;
}

export interface CriticalPathAlert {
  id: string;
  taskCode: string;
  name: string;
  status: string;
  reason: 'blocked' | 'overdue';
  daysLate: number;
  plannedEndDate: string | null;
  completionPercentage: number;
}

export interface RecentActivityItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorRole: string | null;
  fromState: string | null;
  toState: string | null;
  notes: string | null;
  occurredAt: string;
}

export type DashboardWidget =
  | 'acceptedPendingCert'
  | 'nextDrawdown'
  | 'awaitingPayment'
  | 'criticalPath'
  | 'cashVariance'
  | 'burnRate'
  | 'projectHealth'
  | 'riskItems';

export interface DashboardData {
  widgets?: DashboardWidget[];
  healthScore?: ProjectHealthScore;
  kpis: KPI[];
  cashFlow: CashFlowData[];
  phaseProgress: PhaseProgress[];
  topRisks: RiskItem[];
  criticalPathAlerts: CriticalPathAlert[];
  recentActivity: RecentActivityItem[];
}

type ApiEnvelope<T> = { success?: boolean; data?: T };

const unwrap = <T>(response: unknown): T => {
  const body = response as ApiEnvelope<T> | T;
  return body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'data')
    ? ((body as ApiEnvelope<T>).data as T)
    : (body as T);
};

class KPIService {
  /**
   * Get complete dashboard data (single endpoint for efficiency)
   */
  async getDashboardData(): Promise<DashboardData> {
    const response = await apiClient.get('/reports/executive-summary');
    return unwrap<DashboardData>(response);
  }

  /**
   * Get top risks
   */
  async getTopRisks(limit: number = 5): Promise<RiskItem[]> {
    const response = await apiClient.get('/risks/top', {
      params: { limit },
    });
    return unwrap<RiskItem[]>(response) || [];
  }

  /**
   * Sparkline series for an Executive Dashboard card.
   * `metric` is one of: 'cashPosition' | 'burnRate'.
   * Returns `{ period, value }[]` with value in millions.
   */
  async getSparkline(
    projectId: string,
    metric: 'cashPosition' | 'burnRate',
    months: number = 6
  ): Promise<Array<{ period: string; value: number }>> {
    const response: any = await apiClient.get(`/reports/project/${projectId}/sparkline`, {
      params: { metric, months },
    });
    return unwrap<Array<{ period: string; value: number }>>(response) || [];
  }
}

export default new KPIService();
